import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useFeatureFlag } from 'feature-flags';
import FlowContextDataSlideOut from 'components/FlowBuilder/FlowFileUpload/FlowContextDataSlideOut/FlowContextDataSlideOut';
import { FileActionRunnerProvider } from 'components/FlowBuilder/FlowFileUpload/FileActionRunnerContext';
import ContextDataSlideOut from './ContextDataSlideOut';

type ContextDataSlideOutContextValue = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
};

const ContextDataSlideOutContext = createContext<ContextDataSlideOutContextValue | null>(null);

export const useContextDataSlideOut = (): ContextDataSlideOutContextValue | null =>
  useContext(ContextDataSlideOutContext);

type ContextDataSlideOutProviderProps = {
  children: React.ReactNode;
};

export const ContextDataSlideOutProvider: React.FC<ContextDataSlideOutProviderProps> = ({ children }) => {
  const isContextDataQueueV2Enabled = useFeatureFlag('tx_context-data-queue-v2');
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const value = useMemo(
    () => ({
      isOpen,
      open,
      close,
      toggle,
    }),
    [isOpen, open, close, toggle],
  );

  return (
    <ContextDataSlideOutContext.Provider value={value}>
      <FileActionRunnerProvider>
        {children}
        {isContextDataQueueV2Enabled ? (
          <FlowContextDataSlideOut isOpen={isOpen} onClickClose={close} />
        ) : (
          <ContextDataSlideOut isOpen={isOpen} onClickClose={close} />
        )}
      </FileActionRunnerProvider>
    </ContextDataSlideOutContext.Provider>
  );
};
